import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { trackPurchase, trackSubscribe } from '../utils/fbPixel';
import { CheckCircle, Loader2, XCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { login } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | failed
  const [attempts, setAttempts] = useState(0);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);

  useEffect(() => {
    if (!sessionId) { setStatus('failed'); return; }
    poll(0);
  }, [sessionId]);

  const poll = async (attempt) => {
    if (attempt >= 8) { setStatus('failed'); return; }
    try {
      const res = await axios.get(`${API}/stripe/checkout-status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setStatus('success');
        trackPurchase(249, 'USD');
        trackSubscribe(249, 'USD');
        
        // Auto-login if credentials available
        if (res.data.auto_login_ready && res.data.username && !autoLoginAttempted) {
          setAutoLoginAttempted(true);
          // Get password from sessionStorage (saved during registration)
          const savedPassword = sessionStorage.getItem('reg_password');
          if (savedPassword) {
            try {
              await login(res.data.username, savedPassword);
              sessionStorage.removeItem('reg_password');
              setTimeout(() => navigate('/'), 2000);
              return;
            } catch (err) {
              console.error('Auto-login failed:', err);
            }
          }
        }
      } else if (res.data.status === 'expired') {
        setStatus('failed');
      } else {
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2500);
      }
    } catch {
      setTimeout(() => poll(attempt + 1), 2500);
    }
  };

  return (
    <div className="min-h-screen app-shell flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <Loader2 size={48} className="text-cyan-400 animate-spin mx-auto mb-6" />
            <h2 className="font-heading text-xl font-bold text-slate-900 tracking-tight mb-2">Processing your payment...</h2>
            <p className="text-slate-400">Please wait while we confirm your subscription.</p>
            <div className="mt-4 flex justify-center gap-1">
              {[...Array(8)].map((_, i) => (
                <div key={i} className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i <= attempts ? 'bg-cyan-400' : 'bg-slate-700'
                }`} />
              ))}
            </div>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(22,163,74,0.15)' }}>
              <CheckCircle size={48} className="text-green-500" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-3">You're all set! 🎉</h2>
            <p className="text-slate-400 mb-2">Your subscription is active.</p>
            <p className="text-slate-400 text-sm mb-4">$249/month · Cancel anytime from your billing settings</p>
            <p className="text-blue-400 text-sm mb-8">Redirecting to your dashboard...</p>
            <Loader2 size={24} className="text-blue-400 animate-spin mx-auto" />
          </>
        )}
        {status === 'failed' && (
          <>
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(239,68,68,0.15)' }}>
              <XCircle size={48} className="text-red-500" />
            </div>
            <h2 className="font-heading text-xl font-bold text-slate-900 tracking-tight mb-3">Payment not confirmed</h2>
            <p className="text-slate-400 mb-8">
              We couldn't confirm your payment. If you were charged, please contact support. Otherwise, try registering again.
            </p>
            <Link to="/register">
              <Button className="w-full h-11" style={{ background: 'hsl(191,95%,43%)', color: 'hsl(222,47%,11%)' }}>
                Try Again
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
